import type { DispatchOperation } from "@tdelivr/api-client/generated/dispatch/dispatch-operations-read-model.js";
import { classOfStatus } from "@tdelivr/design-tokens";

import { absent, at, field, optional, type RecordDetail } from "./record-detail.ts";

/**
 * What the demand queue does to the rows DSP-05 returns, away from any rendering.
 *
 * The same split as `triage.ts`, for the same reason: searching, ordering and paging are decisions
 * that can be stated and tested without a browser, and a component that does them inline can only be
 * checked by rendering it.
 *
 * Everything here works over the rows the screen already holds. DSP-05 pages by keyset with a `limit`
 * of at most 200 and publishes no total, no search term and no sort — R-01 and R-04 in
 * `docs/design/DESIGN_RECONCILIATION.md` — so the standard data-grid toolbar the design draws is only
 * buildable over a bounded set. A live dispatch queue is bounded by its nature: what is waiting for a
 * driver right now is not thousands of rows, and if it were, this screen would not help anybody. When
 * the service says there is more, the screen says so rather than letting a partial queue read as
 * whole.
 */

/** The sort options the design's toolbar offers, in its order and its words. */
export type QueueOrder = "default" | "state" | "az" | "za";

export interface QueueQuery {
  /** Free text over every value on the row, as the design's grid searches. */
  readonly search: string;
  /** One state label, or undefined for all of them. */
  readonly status?: string;
  readonly order: QueueOrder;
  /** 1-based, as the pager shows it. */
  readonly page: number;
  readonly perPage: number;
}

export interface QueuePage {
  /** The rows on the current page. */
  readonly rows: readonly DispatchOperation[];
  /** Every row that matched, across all pages — what an export writes. */
  readonly all: readonly DispatchOperation[];
  readonly total: number;
  readonly pageCount: number;
  /** Every state label present in the fetched set, for the filter. */
  readonly states: readonly string[];
}

/**
 * The saga's vocabulary in an operator's words.
 *
 * `finding_candidates` is a good name for a state machine and a poor one for somebody deciding
 * whether to intervene. Three of the five statuses are the same state class, so the class label alone
 * would say "In progress" for looking, offering and assigning alike — states an operator would act on
 * differently. The glyph and the hue still come from the class.
 */
export function readableStatus(status: DispatchOperation["status"]): string {
  switch (status) {
    case "finding_candidates":
      return "Looking for drivers";
    case "offering":
      return "Offered";
    case "assigning":
      return "Assigning";
    case "completed":
      return "Assigned";
    case "failed":
      return "Failed";
  }
}

/**
 * Every word on a row that a search should match.
 *
 * The published values, not the rendered ones, plus the operator wording that is on screen. An
 * operator searching `failed` and an operator searching `Failed` are looking for the same rows.
 */
function searchableText(row: DispatchOperation): string {
  return [
    row.demandId,
    row.processId,
    row.status,
    readableStatus(row.status),
    row.failureReason ?? "",
    row.candidateBatchId ?? "",
    row.offerId ?? "",
    row.assignmentId ?? "",
  ]
    .join(" ")
    .toLowerCase();
}

/**
 * Filter, order and page what the screen holds.
 *
 * `default` is the order DSP-05 answered in — soonest deadline first, then by demand so the ordering
 * is total. That ordering is the service's own and the screen must not quietly replace it: a queue
 * re-sorted by identifier hides the row that is about to run out of time.
 */
export function queuePage(rows: readonly DispatchOperation[], query: QueueQuery): QueuePage {
  const needle = query.search.trim().toLowerCase();
  const matched = rows.filter(
    (row) =>
      (query.status === undefined || readableStatus(row.status) === query.status) &&
      (needle.length === 0 || searchableText(row).includes(needle)),
  );

  const ordered =
    query.order === "default"
      ? matched
      : [...matched].sort((left, right) => {
          if (query.order === "state") {
            return readableStatus(left.status).localeCompare(readableStatus(right.status));
          }
          const compared = left.demandId.localeCompare(right.demandId);
          return query.order === "az" ? compared : -compared;
        });

  const total = ordered.length;
  const pageCount = Math.max(1, Math.ceil(total / query.perPage));
  const page = Math.min(Math.max(1, query.page), pageCount);
  const from = (page - 1) * query.perPage;

  return {
    rows: ordered.slice(from, from + query.perPage),
    all: ordered,
    total,
    pageCount,
    states: [...new Set(rows.map((row) => readableStatus(row.status)))].sort(),
  };
}

/**
 * Which rows the design tints, and how.
 *
 * The design gives a row the background of its state class and nothing else — no inset bar, unlike
 * the exceptions board. Only two of the nine classes are tinted: `failed` and `human`. DSP-05's
 * `failed` is the first; nothing DSP-05 publishes is the second, so no row on this screen is ever
 * violet. That absence is the design's fourth sample row, and it is recorded in R-08.
 */
export function rowTint(row: DispatchOperation): "failed" | undefined {
  return row.status === "failed" ? "failed" : undefined;
}

/**
 * The selected dispatch, for the panel beside the queue.
 *
 * Every line is a field DSP-05 already published on the row the board holds. The projection is
 * unusually complete for this console — it carries the saga's whole shape — so this panel has more
 * to say than most and needs no second request to say it.
 *
 * **Three identifiers are the point of the panel.** `candidateBatchId`, `offerId` and
 * `assignmentId` are how a dispatch is followed across four services, and the queue's own grid has
 * room for none of them. Each is `null` until the saga reaches that step, and a null is dropped
 * rather than drawn: a dispatch that has not offered yet has no offer, which is different from an
 * offer this console could not read.
 *
 * **Two booleans are drawn even when false**, which is the opposite choice and deliberate.
 * `offersCancelled` and `assignmentCancelled` are what distinguish a failure that cleaned up after
 * itself from one that left an offer live, and that is exactly what an operator looking at a failed
 * dispatch needs to know. Absent, they would read as "not cancelled" by assumption rather than by
 * statement.
 *
 * **What is not here is the rider and the pickup.** DSP-05 publishes a `demandId` and no place and
 * no person; the live map's demand pins have the same absence from the same cause, recorded as
 * R-09. A panel inventing either would be inventing the two facts an operator would most want.
 */
export function dispatchDetail(row: DispatchOperation): RecordDetail {
  const classKey = classOfStatus("DSP-04", row.status);
  return {
    headline: readableStatus(row.status),
    identifier: row.demandId,
    source: "DSP-05 · DSP-04",
    ...(classKey === undefined
      ? {}
      : {
          state: {
            classKey,
            label: readableStatus(row.status),
            code: "DSP-04",
            status: row.status,
          },
        }),
    fields: [
      field("Process", row.processId, true),
      field("Attempt", String(row.attempt)),
      ...optional("Requested", at(row.requestedAt)),
      ...optional("Deadline", at(row.deadlineAt)),
      field("Observed", at(row.observedAt) ?? row.observedAt),
      ...optional("Candidate batch", row.candidateBatchId, true),
      ...optional("Offer", row.offerId, true),
      ...optional("Assignment", row.assignmentId, true),
      ...optional("Failure", row.failureReason),
      // Stated rather than implied: see this function's head.
      field("Offers cancelled", row.offersCancelled ? "yes" : "no"),
      field("Assignment cancelled", row.assignmentCancelled ? "yes" : "no"),
      absent(
        "Rider and pickup",
        "DSP-05 publishes a demandId and no person and no place. The live map's demand pins have the same absence from the same cause — R-09.",
      ),
    ],
    note:
      row.status === "failed"
        ? "A failed dispatch found no driver. The two cancelled flags above say whether it cleaned up after itself; nothing here re-runs it."
        : "This is DSP-05's projection of DSP-04's saga, so it is what the dispatcher saw, a moment behind the saga itself.",
  };
}
